import { Check, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { Product, Variant } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card } from '@nucleus-commerce/ui/components/card';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb, useMedia } from '@/features/media';

import {
  groupVariants,
  isNewVariant,
  matchesVariantSearch,
  resolveGroupKey,
  variantRowLabel,
  type VariantReveal,
} from '../schemas/variant-grouping';

/** Anchor the editor scrolls to after replacing placeholder variants. */
export const VARIANTS_SECTION_ID = 'product-variants';

const rowDomId = (variantId: string) => `variant-row-${variantId}`;

/** A column of the variant rows; a host page brings its own. */
export interface VariantColumn {
  id: string;
  header: ReactNode;
  /** A CSS grid track. */
  width: string;
  align?: 'end' | 'center';
  cell: (variant: Variant) => ReactNode;
  /** What the group row shows in this column. */
  summary?: (variants: readonly Variant[]) => ReactNode;
}

/**
 * What the Variants card shows for a page: the master product has one of these, a store's product
 * page another. The card owns the grouping, search, selection and View more; the host owns every
 * field, draft and save.
 */
export interface VariantsCardExtension {
  /** The rows to show, when the host keeps them in step with a form of its own. */
  variants?: readonly Variant[];
  isPending?: boolean;
  columns: readonly VariantColumn[];
  /** View more: everything that does not fit in the row. */
  renderDetail: (variant: Variant) => ReactNode;
  /** Beside the title. */
  badge?: ReactNode;
  /** Under the title. */
  description?: ReactNode;
  /** Between the header and the rows: a failed save, a missing exchange rate. */
  notice?: ReactNode;
  /** Checkboxes and the selection's actions; off for people who may only read. */
  selectable?: boolean;
  renderBulk?: (selected: Variant[]) => ReactNode;
  footer?: ReactNode;
  /** The SKU the search looks at, when not the saved one. */
  skuOf?: (variant: Variant) => string;
  /** The status chip of a row, when it can differ from the saved one. */
  renderStatus?: (variant: Variant) => ReactNode;
  /** Under the row's name: an error hiding under a closed View more. */
  renderRowNote?: (variant: Variant, expanded: boolean) => ReactNode;
  /** Rows holding an error: they and their groups stay on screen. */
  errorIds?: ReadonlySet<string>;
  /** Set a new object to open and bring up the rows of a failed save. */
  reveal?: VariantReveal | null;
  /** The empty state's text. */
  emptyText?: ReactNode;
}

export interface VariantsCardProps extends Omit<VariantsCardExtension, 'variants' | 'isPending'> {
  product: Product;
  /** In position order. */
  variants: readonly Variant[];
  isPending?: boolean;
  error?: unknown;
  onRetry?: () => void;
}

const NO_ERRORS: ReadonlySet<string> = new Set();

const ALIGN = {
  end: 'sm:justify-end sm:text-right',
  center: 'sm:justify-center sm:text-center',
} as const;

function Cell({
  column,
  className,
  children,
}: {
  column: VariantColumn;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="cell"
      // On a phone cells wrap instead of squeezing an input below what it needs.
      style={{ minWidth: column.width }}
      className={cn(
        'flex flex-col max-sm:flex-1',
        column.align === 'end' && 'sm:items-end',
        column.align === 'center' && 'sm:items-center',
        className,
      )}
    >
      {/* The header row doesn't fit a phone, so each cell says what it is there. */}
      <span className="text-muted-foreground mb-1 text-xs sm:hidden">{column.header}</span>
      {children}
    </div>
  );
}

/** A row's status when it is not on sale: Active says nothing. */
export function VariantStatusChip({ status }: { status: Variant['status'] | undefined }) {
  const { t } = useTranslation('common');
  // Undefined for the render in which a row leaves the form.
  if (!status || status === 'active') return null;
  return (
    <Badge variant="secondary" className="px-1.5 py-0 text-[11px] font-semibold">
      {t(`status.${status}`)}
    </Badge>
  );
}

function VariantThumb({ variant }: { variant: Variant }) {
  const { data } = useMedia(variant.mediaId);
  return <MediaThumb media={data} className="size-8 shrink-0 rounded-md border" />;
}

function MoreButton({
  variant,
  expanded,
  onToggle,
}: {
  variant: Variant;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('products');
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={t(expanded ? 'variantsCard.viewLessFor' : 'variantsCard.viewMoreFor', {
        variant: variant.title,
      })}
      onClick={onToggle}
      className="text-primary inline-flex items-center justify-end gap-0.5 py-1 text-[13px] whitespace-nowrap hover:underline"
    >
      {t(expanded ? 'variantsCard.viewLess' : 'variantsCard.viewMore')}
      <ChevronRight
        aria-hidden
        className={cn('size-3.5 transition-transform', expanded ? '-rotate-90' : 'rotate-90')}
      />
    </button>
  );
}

/**
 * The Variants card, on both product pages: rows grouped by an option, searched by title and SKU,
 * selected for the actions on many, each row opening in place with View more, one at a time. A
 * product without options is one row under "Price and stock".
 */
export function VariantsCard({
  product,
  variants,
  isPending = false,
  error = null,
  onRetry,
  columns,
  renderDetail,
  badge,
  description,
  notice,
  selectable = false,
  renderBulk,
  footer,
  skuOf,
  renderStatus,
  renderRowNote,
  errorIds = NO_ERRORS,
  reveal = null,
  emptyText,
}: VariantsCardProps) {
  const { t } = useTranslation(['products', 'common']);
  const options = product.options;
  const single = options.length === 0;
  const [query, setQuery] = useState('');
  const [groupChoice, setGroupChoice] = useState<string | null | undefined>(undefined);
  const [openGroups, setOpenGroups] = useState<Readonly<Record<string, boolean>>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [now] = useState(() => Date.now());

  // Adjust state when the prop moves: the row opens in the same render as the error arrives.
  const [seenReveal, setSeenReveal] = useState(reveal);
  if (reveal !== seenReveal) {
    setSeenReveal(reveal);
    if (reveal?.expand) setExpandedId(reveal.expand);
  }
  useEffect(() => {
    if (!reveal) return;
    document
      .getElementById(rowDomId(reveal.first))
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [reveal]);

  // Removing the only option archives the variants it told apart; the one that stays is the row.
  const rows = useMemo(() => {
    if (!single) return variants;
    const live = variants.filter((variant) => variant.status !== 'archived');
    return live.length > 0 ? live : variants;
  }, [single, variants]);

  const archivedCount = variants.filter((variant) => variant.status === 'archived').length;
  const searching = query.trim() !== '';
  const shown = rows.filter(
    (variant) =>
      errorIds.has(variant.id) ||
      matchesVariantSearch(variant, skuOf ? skuOf(variant) : (variant.sku ?? ''), query),
  );
  const groupKey = resolveGroupKey(options, groupChoice);
  const groups = groupVariants(shown, options, groupKey);
  const openKey = (key: string) => `${groupKey ?? ''}:${key}`;
  const isOpen = (group: (typeof groups)[number], at: number) => {
    if (groupKey === null || searching) return true;
    if (group.variants.some((variant) => errorIds.has(variant.id))) return true;
    return openGroups[openKey(group.key)] ?? at === 0;
  };

  const canSelect = selectable && !single;
  const picked = rows.filter((variant) => selected.has(variant.id));
  const stateOf = (list: readonly Variant[]) => {
    const count = list.filter((variant) => selected.has(variant.id)).length;
    return count === 0 ? false : count === list.length ? true : ('indeterminate' as const);
  };
  const flip = (list: readonly Variant[]) => {
    const on = stateOf(list) !== true;
    setSelected((current) => {
      const next = new Set(current);
      for (const variant of list) {
        if (on) next.add(variant.id);
        else next.delete(variant.id);
      }
      return next;
    });
  };

  const tracks = columns.map((column) => column.width);
  const template = (
    single
      ? [...tracks, 'minmax(0, 1fr)', '5.75rem']
      : [...(canSelect ? ['1rem'] : []), 'minmax(0, 1fr)', ...tracks, '5.75rem']
  ).join(' ');
  const gridStyle = { gridTemplateColumns: template };
  // On a phone a row wraps: name on its own line, then the cells; from `sm` it is the grid.
  const rowClass = 'flex flex-wrap items-center gap-2.5 px-4 py-[7px] sm:grid sm:gap-y-0';

  const toggleExpanded = (id: string) => setExpandedId((current) => (current === id ? null : id));
  const summaryOf = (column: VariantColumn, list: readonly Variant[]) => column.summary?.(list);

  const renderRow = (variant: Variant) => {
    const expanded = expandedId === variant.id;
    const isSelected = selected.has(variant.id);
    const name = variantRowLabel(variant, options, groupKey);
    const cells = columns.map((column) => (
      <Cell key={column.id} column={column}>
        {column.cell(variant)}
      </Cell>
    ));
    const more = (
      <MoreButton
        variant={variant}
        expanded={expanded}
        onToggle={() => toggleExpanded(variant.id)}
      />
    );
    const nameCell = (
      <div
        role="rowheader"
        className="flex min-w-0 items-center gap-2.5 max-sm:grow max-sm:basis-[calc(100%-1.75rem)]"
      >
        {single ? null : <VariantThumb variant={variant} />}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="min-w-0 font-medium">{name}</span>
            {isNewVariant(variant, product, now) ? (
              <Badge className="bg-primary-tint text-primary border-transparent px-1.5 py-0 text-[11px] font-semibold">
                {t('products:variantsCard.new')}
              </Badge>
            ) : null}
            {renderStatus ? renderStatus(variant) : <VariantStatusChip status={variant.status} />}
          </div>
          {renderRowNote?.(variant, expanded)}
        </div>
      </div>
    );
    return (
      <div key={variant.id} id={rowDomId(variant.id)} className="scroll-mt-4">
        <div
          role="row"
          style={gridStyle}
          className={cn(
            rowClass,
            expanded ? 'border-primary/20 border-b' : 'border-border/60 border-b',
            (expanded || isSelected) && 'bg-primary-tint/40',
          )}
        >
          {single ? (
            <>
              {cells}
              {rows.length > 1 ? nameCell : <span className="max-sm:hidden" />}
              {more}
            </>
          ) : (
            <>
              {canSelect ? (
                <Checkbox
                  checked={isSelected}
                  aria-label={t('products:variantsCard.selectOne', { name: variant.title })}
                  onCheckedChange={() => flip([variant])}
                />
              ) : null}
              {nameCell}
              {cells}
              {more}
            </>
          )}
        </div>
        {expanded ? (
          <div
            role="row"
            className="bg-primary-tint/40 border-primary/20 border-b px-4 pt-3.5 pb-4"
          >
            <div role="cell">{renderDetail(variant)}</div>
          </div>
        ) : null}
      </div>
    );
  };

  const header = (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3.5">
      <div className="min-w-48 flex-1">
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold">
            {single ? (
              t('products:variantsCard.priceAndStock')
            ) : (
              <>
                {t('products:variants.title')}
                <span className="text-muted-foreground font-normal">
                  {' '}
                  · {variants.length - archivedCount}
                  {archivedCount > 0
                    ? ` · ${t('products:variantsCard.archivedCount', { count: archivedCount })}`
                    : ''}
                </span>
              </>
            )}
          </h2>
          {badge}
        </div>
        {description ? (
          <p className="text-muted-foreground mt-0.5 text-[13px]">{description}</p>
        ) : null}
      </div>
      {single || variants.length === 0 ? null : (
        <>
          <div className="relative w-44">
            <Search
              aria-hidden
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label={t('products:variantsCard.search')}
              placeholder={t('products:variantsCard.search')}
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          {options.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="h-8 text-[13px]">
                  {groupKey === null
                    ? t('products:variantsCard.noGrouping')
                    : t('products:variantsCard.groupBy', {
                        option: options.find((option) => option.key === groupKey)?.name,
                      })}
                  <ChevronDown aria-hidden className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-44">
                {options.map((option) => (
                  <DropdownMenuItem
                    key={option.key}
                    onSelect={() => setGroupChoice(option.key)}
                    className="justify-between"
                  >
                    {option.name}
                    {groupKey === option.key ? <Check aria-hidden className="size-3.5" /> : null}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onSelect={() => setGroupChoice(null)} className="justify-between">
                  {t('products:variantsCard.noGrouping')}
                  {groupKey === null ? <Check aria-hidden className="size-3.5" /> : null}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </>
      )}
      {canSelect && picked.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-muted-foreground text-[13px]">
            {t('products:variantsCard.selected', { count: picked.length })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-8 px-1.5 text-[13px]"
            onClick={() => setSelected(new Set())}
          >
            {t('products:variantsCard.clear')}
          </Button>
          {renderBulk?.(picked)}
        </div>
      ) : null}
    </div>
  );

  const body = isPending ? (
    <div className="flex flex-col gap-2 p-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-9" />
      ))}
    </div>
  ) : error ? (
    <div className="p-4">
      <ErrorState error={error} onRetry={onRetry} compact />
    </div>
  ) : variants.length === 0 ? (
    <div
      className="flex flex-col items-center gap-2 px-5 py-10 text-center"
      data-tour="catalog.product-edit.variants-empty"
    >
      <p className="text-sm font-medium">{t('products:variants.empty.title')}</p>
      <p className="text-muted-foreground max-w-sm text-sm">{emptyText}</p>
    </div>
  ) : (
    <div className="overflow-x-auto">
      <div
        role="table"
        aria-label={t('products:variants.title')}
        className="text-[13px] sm:min-w-[44rem]"
        data-tour="catalog.product-edit.variants-table"
      >
        <div
          role="row"
          style={gridStyle}
          className={cn(rowClass, 'text-muted-foreground border-b')}
        >
          {single ? null : (
            <>
              {canSelect ? (
                <Checkbox
                  checked={stateOf(shown)}
                  disabled={shown.length === 0}
                  aria-label={t('products:variantsCard.selectAll')}
                  onCheckedChange={() => flip(shown)}
                />
              ) : null}
              <span role="columnheader" className="font-medium">
                {t('products:variants.columns.variant')}
              </span>
            </>
          )}
          {columns.map((column) => (
            <span
              key={column.id}
              role="columnheader"
              className={cn(
                'font-medium whitespace-nowrap max-sm:hidden',
                column.align && ALIGN[column.align],
              )}
            >
              {column.header}
            </span>
          ))}
          {single ? <span className="max-sm:hidden" /> : null}
          <span className="max-sm:hidden" />
        </div>
        {groups.map((group, at) => {
          const open = isOpen(group, at);
          return (
            <div key={group.key}>
              {groupKey === null ? null : (
                <div role="row" style={gridStyle} className={cn(rowClass, 'bg-muted/40 border-b')}>
                  {canSelect ? (
                    <Checkbox
                      checked={stateOf(group.variants)}
                      aria-label={t('products:variantsCard.selectOne', { name: group.label })}
                      onCheckedChange={() => flip(group.variants)}
                    />
                  ) : null}
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() =>
                      setOpenGroups((current) => ({ ...current, [openKey(group.key)]: !open }))
                    }
                    className="flex min-w-0 items-center gap-2 text-left max-sm:grow max-sm:basis-[calc(100%-1.75rem)]"
                  >
                    <ChevronRight
                      aria-hidden
                      className={cn(
                        'text-muted-foreground size-4 shrink-0 transition-transform',
                        open && 'rotate-90',
                      )}
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="font-semibold">{group.label || '—'}</span>
                      <span className="text-muted-foreground text-xs">
                        {t('products:variants.count', { count: group.variants.length })}
                      </span>
                    </span>
                  </button>
                  {columns.map((column) => (
                    <div
                      key={column.id}
                      role="cell"
                      className={cn(
                        'text-muted-foreground flex min-w-0 text-xs whitespace-nowrap tabular-nums empty:hidden max-sm:pl-6',
                        column.align && ALIGN[column.align],
                      )}
                    >
                      {summaryOf(column, group.variants)}
                    </div>
                  ))}
                  <span />
                </div>
              )}
              {open ? group.variants.map(renderRow) : null}
            </div>
          );
        })}
        {shown.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center">
            {t('products:variantsCard.noMatches', { query: query.trim() })}
          </p>
        ) : null}
      </div>
    </div>
  );

  return (
    <Card
      id={VARIANTS_SECTION_ID}
      data-tour="catalog.product-edit.variants"
      tabIndex={-1}
      className="scroll-mt-4 gap-0 overflow-hidden py-0 outline-none"
    >
      {header}
      {notice}
      {body}
      {footer ? (
        <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-[13px]">
          {footer}
        </div>
      ) : null}
    </Card>
  );
}
